import { useEffect, useRef, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/mail-club", label: "Mail Club" },
  { to: "/reading-room", label: "The Reading Room" },
  { to: "/share-your-story", label: "Share Your Story" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/manage", label: "Manage Plan" },
];

function CustomCursor() {
  const dotRef = useRef(null);
  useEffect(() => {
    const dot = dotRef.current;
    if (!dot) return;
    const move = (e) => {
      dot.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
    };
    const over = (e) => {
      const t = e.target;
      if (t.closest("a, button, [data-cursor='hover']")) dot.classList.add("active");
      else dot.classList.remove("active");
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
    };
  }, []);
  return <div ref={dotRef} className="cursor-dot" aria-hidden />;
}

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-md bg-cream/85 border-b border-ink/10" : ""
        }`}
        data-testid="site-nav"
      >
        <div className="editorial-container flex items-center justify-between py-5">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <Logo size="sm" />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {NAV.slice(1).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `nav-link overline text-ink ${isActive ? "active" : ""}`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/mail-club"
              className="btn-pill"
              data-testid="nav-subscribe-cta"
            >
              Subscribe <span aria-hidden>→</span>
            </Link>
          </div>

          <button
            className="lg:hidden text-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-cream lg:hidden pt-24"
            data-testid="mobile-menu"
          >
            <div className="editorial-container flex flex-col gap-6">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 + 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={n.to}
                    className="font-serif text-3xl text-ink"
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Footer() {
  return (
    <footer className="mt-32 pt-24 pb-12 border-t border-ink/10 bg-cream" data-testid="site-footer">
      <div className="editorial-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo size="lg" />
            <p className="mt-10 font-serif text-lg md:text-xl leading-relaxed text-ink/70 max-w-md">
              A monthly print publication for the tender, complicated seasons of pregnancy
              and motherhood — delivered by post.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-ink/50 mb-6">Read</div>
            <ul className="space-y-3 font-serif text-xl">
              <li><Link className="nav-link" to="/mail-club">Mail Club</Link></li>
              <li><Link className="nav-link" to="/subscribe">Subscribe</Link></li>
              <li><Link className="nav-link" to="/reading-room">The Reading Room</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline text-ink/50 mb-6">Correspondence</div>
            <ul className="space-y-3 font-serif text-xl">
              <li><Link className="nav-link" to="/about">About</Link></li>
              <li><Link className="nav-link" to="/faq">FAQ</Link></li>
              <li><Link className="nav-link" to="/manage">Manage my plan</Link></li>
              <li><Link className="nav-link" to="/contact">Contact</Link></li>
            </ul>
            <div className="mt-10 overline text-ink/50">By post</div>
            <p className="mt-3 font-serif text-lg text-ink/70">For mothers, by mothers.</p>
          </div>
        </div>

        <div className="hairline mt-20 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="overline text-ink/50">© {new Date().getFullYear()} The Safe Mama Editorial</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="overline text-ink/50 hover:text-ink transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="overline text-ink/50 hover:text-ink transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link>
          </div>
          <p className="overline text-ink/50" data-testid="footer-credit">
            Published by <span className="font-serif italic normal-case tracking-normal text-ink/70">Banyan Editorials</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-cream text-ink relative">
      <div className="grain-overlay" />
      <Nav />
      <main className="pt-20">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
